import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertCircle } from 'lucide-react';
import { Switch } from '../../../ui/switch';
import apiClient from '../../../../api/apiClient';

export default function PolicyModal({
  open,
  data,
  saving,
  onClose,
  onSave,
  existingPolicies = [],
}) {
  const [form, setForm] = useState(data || {});
  const [spreadInput, setSpreadInput] = useState('');
  const [maxYearsInput, setMaxYearsInput] = useState('');

  // Format currency to Indonesian Rupiah
  // const formatCurrency = value => {
  //   if (!value && value !== 0) return '';
  //   const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d]/g, '')) : value;
  //   if (isNaN(numValue)) return '';
  //   return new Intl.NumberFormat('id-ID', {
  //     style: 'currency',
  //     currency: 'IDR',
  //     minimumFractionDigits: 0,
  //   }).format(numValue);
  // };

  // Parse currency string to number
  // const parseCurrency = value => {
  //   if (!value) return 0;
  //   const numValue = parseFloat(value.replace(/[^\d]/g, ''));
  //   return isNaN(numValue) ? 0 : numValue;
  // };

  // Handle currency input change
  const handleCurrencyChange = (field, value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/[^\d]/g, '');
    if (cleaned === '') {
      setForm({ ...form, [field]: 0 });
      return;
    }
    const numValue = parseInt(cleaned, 10);
    if (!isNaN(numValue)) {
      setForm({ ...form, [field]: numValue });
    }
  };

  // Format currency for display (without Rp prefix for input)
  const formatCurrencyInput = value => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d]/g, '')) : value;
    if (isNaN(numValue) || numValue === 0) return '';
    return new Intl.NumberFormat('id-ID').format(numValue);
  };

  // Function to generate policy code
  const generatePolicyCode = useCallback(() => {
    try {
      if (existingPolicies && Array.isArray(existingPolicies) && existingPolicies.length > 0) {
        // Get all existing codes with POL-XXX format
        const codes = existingPolicies
          .map(p => p?.policy_code)
          .filter(code => code && typeof code === 'string' && /^POL-\d+$/i.test(code))
          .map(code => {
            const numStr = code.replace(/^POL-/i, '');
            return parseInt(numStr, 10);
          })
          .filter(num => !isNaN(num) && num > 0);

        if (codes.length > 0) {
          // Find the first available number (not just max + 1)
          const usedNumbers = new Set(codes);
          let nextNum = 1;
          while (usedNumbers.has(nextNum)) {
            nextNum++;
          }
          return `POL-${String(nextNum).padStart(3, '0')}`;
        }
      }
      // If no existing policies or no valid codes, start from 001
      return 'POL-001';
    } catch (error) {
      console.error('Error generating policy code:', error);
      return 'POL-001';
    }
  }, [existingPolicies]);

  useEffect(() => {
    if (data && data.id) {
      // Edit mode - use existing data
      // Convert spread from decimal to percentage for display
      const editData = {
        ...data,
        spread_display: data.spread ? data.spread * 100 : 40,
        yearly_increment: data.yearly_increment || 0,
        max_years: data.max_years || 10,
        competency_factor: data.competency_factor || 0,
      };
      setForm(editData);
      setSpreadInput(
        editData.spread_display?.toString() || (data.spread ? (data.spread * 100).toString() : '40')
      );
      setMaxYearsInput(editData.max_years?.toString() || '10');
    } else if (!data || !data.id) {
      // New policy - generate code from backend (checks database in real-time)
      const generateCodeFromBackend = async () => {
        try {
          const response = await apiClient.get('/hris/organization/policy/generate-code');
          if (response && response.policy_code) {
            const today = new Date().toISOString().split('T')[0];
            const newForm = {
              policy_code: response.policy_code,
              policy_name: '',
              effective_date: today,
              calculation_method: 'two_point',
              base_salary: 0,
              top_salary: 0,
              spread: 0.4,
              spread_display: 40,
              yearly_increment: 0,
              max_years: 10,
              competency_factor: 0,
              use_tenure_adjustment: true,
              use_competency_adjustment: true,
              competency_multiplier: { '1': 0.8, '2': 0.9, '3': 1.0, '4': 1.1, '5': 1.2 },
              is_active: true,
            };
            setForm(newForm);
            setSpreadInput('40');
            setMaxYearsInput('10');
          }
        } catch (error) {
          console.error('Error generating policy code from backend:', error);
          // Fallback to frontend generation if backend fails
          const today = new Date().toISOString().split('T')[0];
          const newForm = {
            policy_code: generatePolicyCode(),
            policy_name: '',
            effective_date: today,
            calculation_method: 'two_point',
            base_salary: 0,
            top_salary: 0,
            spread: 0.4,
            spread_display: 40,
            yearly_increment: 0,
            max_years: 10,
            competency_factor: 0,
            use_tenure_adjustment: true,
            use_competency_adjustment: true,
            competency_multiplier: { '1': 0.8, '2': 0.9, '3': 1.0, '4': 1.1, '5': 1.2 },
            is_active: true,
          };
          setForm(newForm);
          setSpreadInput('40');
          setMaxYearsInput('10');
        }
      };

      generateCodeFromBackend();
    }
  }, [data, existingPolicies, generatePolicyCode]);

  if (!open) return null;

  const handleSubmit = async e => {
    e.preventDefault();

    // Validate required fields
    if (!form.policy_code || !form.policy_name || !form.effective_date) {
      alert(
        'Mohon lengkapi semua field yang wajib diisi (Kode Kebijakan, Nama Kebijakan, Tanggal Berlaku)'
      );
      return;
    }

    // Validate base_salary and top_salary
    const baseSalary = parseFloat(String(form.base_salary)) || 0;
    const topSalary = parseFloat(String(form.top_salary)) || 0;

    if (baseSalary <= 0) {
      alert('Base Salary harus diisi dan lebih besar dari 0');
      return;
    }

    if (topSalary <= 0) {
      alert('Top Salary harus diisi dan lebih besar dari 0');
      return;
    }

    if (topSalary <= baseSalary) {
      alert('Top Salary harus lebih besar dari Base Salary');
      return;
    }

    // Prepare data to send
    const { spread_display, ...formData } = form;

    // Ensure spread has a value (from spreadInput or default)
    let spreadValue = 0.4;
    if (spreadInput && spreadInput !== '' && !isNaN(parseFloat(spreadInput))) {
      spreadValue = parseFloat(spreadInput) / 100;
    } else if (formData.spread !== null && formData.spread !== undefined) {
      spreadValue = formData.spread;
    }

    // Ensure max_years has a value
    let maxYearsValue = 10;
    if (maxYearsInput && maxYearsInput !== '' && !isNaN(parseInt(maxYearsInput))) {
      maxYearsValue = parseInt(maxYearsInput);
    } else if (formData.max_years !== null && formData.max_years !== undefined) {
      maxYearsValue = formData.max_years;
    }

    const dataToSave = {
      ...formData,
      base_salary: baseSalary,
      top_salary: topSalary,
      spread: spreadValue,
      total_grades: formData.total_grades || 10, // Default 10 for backward compatibility
      yearly_increment: parseFloat(String(formData.yearly_increment)) || 0,
      max_years: maxYearsValue,
      competency_factor: parseFloat(String(formData.competency_factor)) || 0,
      use_tenure_adjustment:
        formData.use_tenure_adjustment !== undefined ? formData.use_tenure_adjustment : true,
      use_competency_adjustment:
        formData.use_competency_adjustment !== undefined
          ? formData.use_competency_adjustment
          : true,
      is_active: formData.is_active !== undefined ? formData.is_active : true,
      calculation_method: formData.calculation_method || 'two_point',
      end_date: formData.end_date || null,
      competency_multiplier: formData.competency_multiplier || {
        '1': 0.8,
        '2': 0.9,
        '3': 1.0,
        '4': 1.1,
        '5': 1.2,
      },
    };

    // Remove fields that don't need to be sent
    delete dataToSave.spread_display;

    // Save and get response to update policy_code if changed by backend
    const result = await onSave(dataToSave);

    // If save is successful and returns updated data, update form with new policy_code
    if (
      result &&
      result.policy &&
      result.policy.policy_code &&
      result.policy.policy_code !== dataToSave.policy_code
    ) {
      // Update form with new policy_code from backend
      setForm(prev => ({ ...prev, policy_code: result.policy.policy_code }));

      // Show notification that policy code was changed
      alert(
        `Kode kebijakan telah diubah dari ${dataToSave.policy_code} menjadi ${result.policy.policy_code} karena kode tersebut sudah digunakan.`
      );
    }

    // Note: Modal will be closed by onSave callback in OrganizationStructure
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden animate-modal flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-4 space-y-4 overflow-y-auto flex-1">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {form?.id ? 'Edit' : 'Tambah'} Kebijakan Gaji
              </h3>
              <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                Two Point Method (Permenaker No.1/2017): Gaji = Mid Salary + (Masa Kerja × Kenaikan)
                + (Kompetensi × Faktor)
              </p>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Informasi Kebijakan</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Kode Kebijakan <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form?.policy_code || ''}
                    onChange={e => setForm({ ...form, policy_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-gray-50"
                    required
                    readOnly={!form?.id}
                    title={
                      !form?.id
                        ? 'Kode kebijakan di-generate otomatis'
                        : 'Kode kebijakan dapat diubah saat edit'
                    }
                  />
                  {!form?.id && (
                    <p className="text-xs text-gray-500">Kode kebijakan di-generate otomatis</p>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Nama Kebijakan <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form?.policy_name || ''}
                    onChange={e => setForm({ ...form, policy_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Tanggal Berlaku <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form?.effective_date || ''}
                    onChange={e => setForm({ ...form, effective_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Tanggal Berakhir</label>
                  <input
                    type="date"
                    value={form?.end_date || ''}
                    onChange={e => setForm({ ...form, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h4 className="font-semibold">Parameter Two Point</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Base Salary (Level 1) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      Rp
                    </span>
                    <input
                      type="text"
                      value={formatCurrencyInput(form?.base_salary || 0)}
                      onChange={e => handleCurrencyChange('base_salary', e.target.value)}
                      className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      required
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Gaji terendah (level jabatan 1)</p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Top Salary (Level Max) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      Rp
                    </span>
                    <input
                      type="text"
                      value={formatCurrencyInput(form?.top_salary || 0)}
                      onChange={e => handleCurrencyChange('top_salary', e.target.value)}
                      className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      required
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Gaji tertinggi (level max)</p>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Spread (%) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={spreadInput}
                    onChange={e => {
                      const value = e.target.value;
                      setSpreadInput(value);
                      // Update form when user types
                      if (value === '' || value === null) {
                        setForm({ ...form, spread_display: null, spread: null });
                      } else {
                        const percentValue = parseFloat(value);
                        if (!isNaN(percentValue) && percentValue >= 0) {
                          setForm({
                            ...form,
                            spread: percentValue / 100,
                            spread_display: percentValue,
                          });
                        }
                      }
                    }}
                    onBlur={e => {
                      // Only set to default if truly empty on blur
                      const value = e.target.value;
                      if (!value || value === '' || value === null || isNaN(parseFloat(value))) {
                        setForm({ ...form, spread: 0.4, spread_display: 40 });
                        setSpreadInput('40');
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    required
                    min="0"
                    max="100"
                  />
                  <p className="text-xs text-gray-500">Rentang min-max per level (default: 40%)</p>
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h4 className="font-semibold">Penyesuaian</h4>
              <div className="space-y-4">
                {/* First row */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Kenaikan per Tahun (Rp)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                        Rp
                      </span>
                      <input
                        type="text"
                        value={formatCurrencyInput(form?.yearly_increment || 0)}
                        onChange={e => handleCurrencyChange('yearly_increment', e.target.value)}
                        className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Max Tahun Dihitung</label>
                    <input
                      type="number"
                      value={maxYearsInput}
                      onChange={e => {
                        const value = e.target.value;
                        setMaxYearsInput(value);
                        // Update form when user types
                        if (value === '' || value === null) {
                          setForm({ ...form, max_years: null });
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue) && numValue >= 0) {
                            setForm({ ...form, max_years: numValue });
                          }
                        }
                      }}
                      onBlur={e => {
                        // Only set to default if truly empty on blur
                        const value = e.target.value;
                        if (!value || value === '' || value === null || isNaN(parseInt(value))) {
                          setForm({ ...form, max_years: 10 });
                          setMaxYearsInput('10');
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      min="1"
                      max="50"
                    />
                  </div>
                </div>

                {/* Second row */}
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Faktor Kompetensi (Rp per level)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      Rp
                    </span>
                    <input
                      type="text"
                      value={formatCurrencyInput(form?.competency_factor || 0)}
                      onChange={e => handleCurrencyChange('competency_factor', e.target.value)}
                      className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Tambahan per level kompetensi (1-5)</p>
                </div>
              </div>

              {/* Toggle Switches */}
              <div className="space-y-3 mt-6">
                {/* Enable Tenure */}
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">
                        Aktifkan Masa Kerja
                      </h5>
                      <p className="text-xs text-gray-600">
                        Hitung penyesuaian berdasarkan masa kerja
                      </p>
                    </div>
                    <Switch
                      checked={form?.use_tenure_adjustment || false}
                      onCheckedChange={checked =>
                        setForm({ ...form, use_tenure_adjustment: checked })
                      }
                    />
                  </div>
                </div>

                {/* Enable Competency */}
                <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">
                        Aktifkan Kompetensi
                      </h5>
                      <p className="text-xs text-gray-600">
                        Hitung penyesuaian berdasarkan level kompetensi
                      </p>
                    </div>
                    <Switch
                      checked={form?.use_competency_adjustment || false}
                      onCheckedChange={checked =>
                        setForm({ ...form, use_competency_adjustment: checked })
                      }
                    />
                  </div>
                </div>

                {/* Policy Status */}
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-1">Status Kebijakan</h5>
                      <p className="text-xs text-gray-600">Hanya 1 kebijakan boleh aktif</p>
                    </div>
                    <Switch
                      checked={form?.is_active !== false}
                      onCheckedChange={checked => setForm({ ...form, is_active: checked })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 font-medium"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 font-medium"
            >
              {saving ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
